import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlayerStore } from '@/store/player-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, User, Coins, Trophy, Calendar, Heart } from 'lucide-react';

const AVATAR_OPTIONS = [
  { id: 'avatar1', color: 'from-red-500 to-red-700', icon: '♠' },
  { id: 'avatar2', color: 'from-blue-500 to-blue-700', icon: '♥' },
  { id: 'avatar3', color: 'from-green-500 to-green-700', icon: '♣' },
  { id: 'avatar4', color: 'from-amber-500 to-amber-700', icon: '♦' },
  { id: 'avatar5', color: 'from-purple-500 to-purple-700', icon: '★' },
  { id: 'avatar6', color: 'from-pink-500 to-pink-700', icon: '♚' },
  { id: 'avatar7', color: 'from-cyan-500 to-cyan-700', icon: '♛' },
  { id: 'avatar8', color: 'from-indigo-500 to-indigo-700', icon: '♜' },
];

export default function ProfileSelector() {
  const { profiles, currentProfile, loadProfiles, selectProfile, createNewProfile, performCheckIn, claimBankruptcyRelief } = usePlayerStore();
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATAR_OPTIONS[0].id);
  const [checkInResult, setCheckInResult] = useState<{ success: boolean; reward: number } | null>(null);
  const [reliefResult, setReliefResult] = useState<{ success: boolean; amount: number; remaining: number } | null>(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  const handleCreateProfile = async () => {
    if (!newName.trim()) return;
    await createNewProfile(newName.trim(), selectedAvatar);
    setIsCreating(false);
    setNewName('');
  };

  const handleCheckIn = async () => {
    const result = await performCheckIn();
    setCheckInResult(result);
    setTimeout(() => setCheckInResult(null), 3000);
  };

  const handleClaimRelief = async () => {
    const result = await claimBankruptcyRelief();
    setReliefResult(result);
    setTimeout(() => setReliefResult(null), 3000);
  };

  if (currentProfile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-16 h-16 rounded-full bg-gradient-to-br flex items-center justify-center text-2xl`}>
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{currentProfile.name}</h3>
            <p className="text-slate-400">ID: {currentProfile.id.slice(0, 8)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-900/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-emerald-400 mb-1">
              <Coins className="w-4 h-4" />
              <span className="text-sm">筹码</span>
            </div>
            <p className="text-2xl font-bold text-white">{currentProfile.chips.toLocaleString()}</p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-400 mb-1">
              <Trophy className="w-4 h-4" />
              <span className="text-sm">积分</span>
            </div>
            <p className="text-2xl font-bold text-white">{currentProfile.score.toLocaleString()}</p>
          </div>
        </div>

        {/* 每日功能 */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={handleCheckIn}
            variant="outline"
            className="flex-1 border-amber-500/50 hover:bg-amber-500/10"
          >
            <Calendar className="w-4 h-4 mr-2 text-amber-400" />
            每日签到
          </Button>
          
          {currentProfile.chips < 100 && (
            <Button
              onClick={handleClaimRelief}
              variant="outline"
              className="flex-1 border-red-500/50 hover:bg-red-500/10"
            >
              <Heart className="w-4 h-4 mr-2 text-red-400" />
              破产救济 ({3 - currentProfile.dailyReliefCount}/3)
            </Button>
          )}
        </div>

        {/* 结果提示 */}
        <AnimatePresence>
          {checkInResult?.success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-center"
            >
              签到成功！获得 {checkInResult.reward} 筹码
            </motion.div>
          )}
          {reliefResult?.success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-center"
            >
              领取成功！获得 {reliefResult.amount} 筹码（剩余 {reliefResult.remaining} 次）
            </motion.div>
          )}
        </AnimatePresence>

        {/* 统计信息 */}
        <div className="bg-slate-900/30 rounded-xl p-4 mb-4">
          <h4 className="text-sm font-medium text-slate-400 mb-3">游戏统计</h4>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-slate-500">总场次</span>
            <span className="text-white text-right">{currentProfile.stats.totalGames}</span>
            <span className="text-slate-500">胜率</span>
            <span className="text-white text-right">{currentProfile.stats.winRate.toFixed(1)}%</span>
            <span className="text-slate-500">连胜</span>
            <span className="text-white text-right">{currentProfile.stats.currentStreak}</span>
            <span className="text-slate-500">最大赢取</span>
            <span className="text-white text-right">{currentProfile.stats.biggestPot.toLocaleString()}</span>
          </div>
        </div>

        <Button
          onClick={() => selectProfile('')}
          variant="ghost"
          className="w-full text-slate-400 hover:text-white"
        >
          切换档案
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-4">选择玩家档案</h2>
      
      {profiles.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>还没有玩家档案</p>
          <p className="text-sm">创建一个新档案开始游戏</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {profiles.map((profile) => (
            <motion.button
              key={profile.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => selectProfile(profile.id)}
              className="flex items-center gap-3 p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl border border-slate-700 transition-colors text-left"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{profile.name}</p>
                <p className="text-sm text-emerald-400">{profile.chips.toLocaleString()} 筹码</p>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* 创建新档案 */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogTrigger asChild>
          <Button className="w-full mt-4" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            创建新档案
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">创建新档案</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">玩家名称</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="输入你的昵称"
                className="bg-slate-800 border-slate-700 text-white"
                maxLength={12}
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">选择头像</label>
              <div className="grid grid-cols-4 gap-2">
                {AVATAR_OPTIONS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`w-14 h-14 rounded-full bg-gradient-to-br ${avatar.color} flex items-center justify-center text-xl transition-all ${
                      selectedAvatar === avatar.id 
                        ? 'ring-2 ring-amber-400 scale-110' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    {avatar.icon}
                  </button>
                ))}
              </div>
            </div>
            <Button 
              onClick={handleCreateProfile}
              disabled={!newName.trim()}
              className="w-full"
            >
              创建档案
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
